import { useCallback, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Element } from "@/components/elements/UIElement";
import { parseElements } from "@/lib/utils";
import { request } from "@/lib/request";
import { useUser } from "../useUserProvider";
import {
  ElementsQueryData,
  elementsQueryKey,
  isElementsPreconditionFailedError,
  toElementsEtag,
} from "./elementsQuery";
import {
  normalizeElementsResponse,
  serializeElementsPayload,
} from "./elementsTransport";

const CONFLICT_TOAST_MESSAGE =
  "Dashboard changed on the server. Local changes are kept but not synced yet.";

class ManualConflictResolutionError extends Error {
  constructor() {
    super("Elements conflict requires manual resolution.");
  }
}

type MutationContext = {
  previousData: ElementsQueryData | null;
};

type MutationResult = {
  elementsVersion: number;
  map: Record<string, Element>;
  requests: ElementsQueryData["requests"];
};

type MutationPerformResult = {
  elementsVersion: number;
  element?: Element | null;
};

type RetryPlan = {
  baseData: ElementsQueryData;
  buildMap: (baseMap: Record<string, Element>) => Record<string, Element> | null;
  perform: (
    expectedVersion: number,
    map: Record<string, Element>,
  ) => Promise<MutationPerformResult>;
  applyServerResult?: (
    map: Record<string, Element>,
    result: MutationPerformResult,
  ) => Record<string, Element>;
};

const getEmptyResult = (baseData: ElementsQueryData): MutationResult => ({
  elementsVersion: baseData.elementsVersion,
  map: parseElements(baseData.elements),
  requests: baseData.requests,
});

const buildUpdatedMap = (
  baseMap: Record<string, Element>,
  elementId: string,
  config: Record<string, unknown>,
): Record<string, Element> | null => {
  if (!baseMap[elementId]) {
    return null;
  }

  return {
    ...baseMap,
    [elementId]: {
      ...baseMap[elementId],
      config,
    },
  };
};

const buildCreatedMap = (
  baseMap: Record<string, Element>,
  element: Element,
): Record<string, Element> | null => {
  const parentId = element.parentId || "";
  if (!parentId || !baseMap[parentId]) {
    return null;
  }

  return {
    ...baseMap,
    [element.id]: element,
    [parentId]: {
      ...baseMap[parentId],
      childrenIds: [...(baseMap[parentId].childrenIds || []), element.id],
    },
  };
};

const buildDeletedMap = (
  baseMap: Record<string, Element>,
  elementId: string,
): Record<string, Element> | null => {
  const element = baseMap[elementId];
  if (!element || !element.parentId || !baseMap[element.parentId]) {
    return null;
  }

  const nextMap: Record<string, Element> = {
    ...baseMap,
    [element.parentId]: {
      ...baseMap[element.parentId],
      childrenIds: (baseMap[element.parentId].childrenIds || []).filter(
        (id) => id !== elementId,
      ),
    },
  };

  delete nextMap[elementId];
  return nextMap;
};

const parseConfigObject = (value: unknown): Record<string, unknown> => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return {};
    }
  }

  return {};
};

const normalizeCreatedElement = (value: unknown): Element | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const raw = value as Partial<Element> & { config?: unknown };
  if (typeof raw.id !== "string" || typeof raw.name !== "string") {
    return null;
  }

  return {
    id: raw.id,
    name: raw.name,
    parentId:
      raw.parentId === null || typeof raw.parentId === "string"
        ? raw.parentId
        : null,
    childrenIds: Array.isArray(raw.childrenIds)
      ? raw.childrenIds.filter((id): id is string => typeof id === "string")
      : [],
    config: parseConfigObject(raw.config),
  };
};

const reconcileCreatedElement = ({
  optimisticMap,
  requestedId,
  createdElement,
}: {
  optimisticMap: Record<string, Element>;
  requestedId: string;
  createdElement: Element;
}): Record<string, Element> => {
  const nextMap = { ...optimisticMap };
  const optimisticElement = nextMap[requestedId];

  if (requestedId !== createdElement.id && optimisticElement) {
    delete nextMap[requestedId];
  }

  const parentId = createdElement.parentId ?? optimisticElement?.parentId ?? null;
  if (parentId && nextMap[parentId]) {
    const replacedChildren = (nextMap[parentId].childrenIds || []).map((id) =>
      id === requestedId ? createdElement.id : id,
    );

    nextMap[parentId] = {
      ...nextMap[parentId],
      childrenIds: replacedChildren.includes(createdElement.id)
        ? replacedChildren
        : [...replacedChildren, createdElement.id],
    };
  }

  nextMap[createdElement.id] = {
    ...createdElement,
    parentId,
    childrenIds: createdElement.childrenIds || [],
  };

  return nextMap;
};

export const useElementMutations = ({
  profileId,
}: {
  profileId?: string | null;
} = {}) => {
  const { accessToken } = useUser();
  const queryClient = useQueryClient();
  const [hasUnsyncedChanges, setHasUnsyncedChanges] = useState(false);

  const queryKey = useMemo(() => elementsQueryKey(profileId), [profileId]);

  const getCachedElements = useCallback((): ElementsQueryData | null => {
    if (!profileId) {
      return null;
    }

    return queryClient.getQueryData<ElementsQueryData>(queryKey) || null;
  }, [profileId, queryClient, queryKey]);

  const setCachedElements = useCallback(
    ({
      map,
      elementsVersion,
      requests,
    }: {
      map: Record<string, Element>;
      elementsVersion: number;
      requests: ElementsQueryData["requests"];
    }) => {
      queryClient.setQueryData<ElementsQueryData>(queryKey, {
        elements: Object.values(map),
        requests,
        elementsVersion,
      });
    },
    [queryClient, queryKey],
  );

  const fetchLatestElements = useCallback(async (): Promise<ElementsQueryData> => {
    if (!profileId) {
      throw new Error("Profile ID is required to fetch elements.");
    }

    const response = await request.get(`/profile/${profileId}/elements`);
    return normalizeElementsResponse(response.data?.data);
  }, [profileId]);

  const executeWithRetry = useCallback(
    async ({
      baseData,
      buildMap,
      perform,
      applyServerResult,
    }: RetryPlan): Promise<MutationResult> => {
      const initialMap = buildMap(parseElements(baseData.elements));
      if (!initialMap) {
        return getEmptyResult(baseData);
      }

      try {
        const initialResult = await perform(baseData.elementsVersion, initialMap);
        return {
          elementsVersion: initialResult.elementsVersion,
          map: applyServerResult
            ? applyServerResult(initialMap, initialResult)
            : initialMap,
          requests: baseData.requests,
        };
      } catch (error) {
        if (!isElementsPreconditionFailedError(error)) {
          throw error;
        }
      }

      const latestData = await fetchLatestElements();
      const rebasedMap = buildMap(parseElements(latestData.elements));
      if (!rebasedMap) {
        return {
          elementsVersion: latestData.elementsVersion,
          map: parseElements(latestData.elements),
          requests: latestData.requests,
        };
      }

      setCachedElements({
        map: rebasedMap,
        elementsVersion: latestData.elementsVersion,
        requests: latestData.requests,
      });

      try {
        const rebasedResult = await perform(
          latestData.elementsVersion,
          rebasedMap,
        );
        return {
          elementsVersion: rebasedResult.elementsVersion,
          map: applyServerResult
            ? applyServerResult(rebasedMap, rebasedResult)
            : rebasedMap,
          requests: latestData.requests,
        };
      } catch (retryError) {
        if (isElementsPreconditionFailedError(retryError)) {
          throw new ManualConflictResolutionError();
        }

        throw retryError;
      }
    },
    [fetchLatestElements, setCachedElements],
  );

  const baseMutationOptions = useMemo(
    () => ({
      mutationKey: ["elements", "mutation", profileId || "unknown"] as const,
      scope: {
        id: `elements:${profileId || "unknown"}`,
      },
      onError: (
        error: unknown,
        _variables: unknown,
        context: MutationContext | undefined,
      ) => {
        if (error instanceof ManualConflictResolutionError) {
          setHasUnsyncedChanges(true);
          toast.error(CONFLICT_TOAST_MESSAGE);
          return;
        }

        if (context?.previousData) {
          queryClient.setQueryData<ElementsQueryData>(queryKey, context.previousData);
        }
      },
      onSuccess: (result: MutationResult) => {
        setCachedElements({
          map: result.map,
          elementsVersion: result.elementsVersion,
          requests: result.requests,
        });
        setHasUnsyncedChanges(false);
      },
      onSettled: async () => {
        if (!profileId) {
          return;
        }

        await queryClient.invalidateQueries({
          queryKey,
        });
      },
    }),
    [profileId, queryClient, queryKey, setCachedElements],
  );

  const syncMutation = useMutation<
    MutationResult,
    unknown,
    { baseData: ElementsQueryData; nextMap: Record<string, Element> },
    MutationContext
  >({
    ...baseMutationOptions,
    mutationKey: ["elements", "sync", profileId || "unknown"],
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey,
      });
      const previousData = getCachedElements();

      setCachedElements({
        map: variables.nextMap,
        elementsVersion: variables.baseData.elementsVersion,
        requests: variables.baseData.requests,
      });

      return {
        previousData,
      };
    },
    mutationFn: async (variables) => {
      if (!accessToken) {
        throw new Error("You need to be logged in to sync elements.");
      }

      if (!profileId) {
        throw new Error("Profile ID is required to sync elements.");
      }

      return executeWithRetry({
        baseData: variables.baseData,
        buildMap: () => variables.nextMap,
        perform: async (expectedVersion, map) => {
          const response = await request.put(
            `/profile/${profileId}/elements`,
            serializeElementsPayload(Object.values(map)),
            {
              headers: {
                "If-Match": toElementsEtag(expectedVersion),
              },
            },
          );

          return {
            elementsVersion: Number(response.data?.data?.elementsVersion || 0),
          };
        },
      });
    },
  });

  const createMutation = useMutation<
    MutationResult,
    unknown,
    { baseData: ElementsQueryData; element: Element },
    MutationContext
  >({
    ...baseMutationOptions,
    mutationKey: ["elements", "create", profileId || "unknown"],
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey,
      });
      const previousData = getCachedElements();

      return {
        previousData,
      };
    },
    mutationFn: async (variables) => {
      if (!accessToken) {
        throw new Error("You need to be logged in to create elements.");
      }

      if (!profileId) {
        throw new Error("Profile ID is required to create elements.");
      }

      return executeWithRetry({
        baseData: variables.baseData,
        buildMap: (baseMap) => buildCreatedMap(baseMap, variables.element),
        perform: async (expectedVersion) => {
          const response = await request.post(
            `/profile/${profileId}/elements`,
            {
              id: variables.element.id,
              name: variables.element.name,
              parentId: variables.element.parentId,
              config: JSON.stringify(variables.element.config),
            },
            {
              headers: {
                "If-Match": toElementsEtag(expectedVersion),
              },
            },
          );

          return {
            elementsVersion: Number(response.data?.data?.elementsVersion || 0),
            element: normalizeCreatedElement(response.data?.data?.element),
          };
        },
        applyServerResult: (map, result) => {
          if (!result.element) {
            return map;
          }

          return reconcileCreatedElement({
            optimisticMap: map,
            requestedId: variables.element.id,
            createdElement: result.element,
          });
        },
      });
    },
  });

  const updateMutation = useMutation<
    MutationResult,
    unknown,
    {
      baseData: ElementsQueryData;
      elementId: string;
      config: Record<string, unknown>;
    },
    MutationContext
  >({
    ...baseMutationOptions,
    mutationKey: ["elements", "update", profileId || "unknown"],
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey,
      });
      const previousData = getCachedElements();

      const optimisticMap =
        buildUpdatedMap(
          parseElements(variables.baseData.elements),
          variables.elementId,
          variables.config,
        ) || parseElements(variables.baseData.elements);

      setCachedElements({
        map: optimisticMap,
        elementsVersion: variables.baseData.elementsVersion,
        requests: variables.baseData.requests,
      });

      return {
        previousData,
      };
    },
    mutationFn: async (variables) => {
      if (!accessToken) {
        throw new Error("You need to be logged in to update elements.");
      }

      if (!profileId) {
        throw new Error("Profile ID is required to update elements.");
      }

      return executeWithRetry({
        baseData: variables.baseData,
        buildMap: (baseMap) =>
          buildUpdatedMap(baseMap, variables.elementId, variables.config),
        perform: async (expectedVersion, map) => {
          const response = await request.put(
            `/profile/${profileId}/elements/${variables.elementId}`,
            {
              name: map[variables.elementId].name,
              parentId: map[variables.elementId].parentId,
              config: JSON.stringify(map[variables.elementId].config),
            },
            {
              headers: {
                "If-Match": toElementsEtag(expectedVersion),
              },
            },
          );

          return {
            elementsVersion: Number(response.data?.data?.elementsVersion || 0),
          };
        },
      });
    },
  });

  const deleteMutation = useMutation<
    MutationResult,
    unknown,
    { baseData: ElementsQueryData; elementId: string },
    MutationContext
  >({
    ...baseMutationOptions,
    mutationKey: ["elements", "delete", profileId || "unknown"],
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey,
      });
      const previousData = getCachedElements();

      const optimisticMap =
        buildDeletedMap(parseElements(variables.baseData.elements), variables.elementId) ||
        parseElements(variables.baseData.elements);

      setCachedElements({
        map: optimisticMap,
        elementsVersion: variables.baseData.elementsVersion,
        requests: variables.baseData.requests,
      });

      return {
        previousData,
      };
    },
    mutationFn: async (variables) => {
      if (!accessToken) {
        throw new Error("You need to be logged in to delete elements.");
      }

      if (!profileId) {
        throw new Error("Profile ID is required to delete elements.");
      }

      return executeWithRetry({
        baseData: variables.baseData,
        buildMap: (baseMap) => buildDeletedMap(baseMap, variables.elementId),
        perform: async (expectedVersion) => {
          const response = await request.delete(
            `/profile/${profileId}/elements/${variables.elementId}`,
            {
              headers: {
                "If-Match": toElementsEtag(expectedVersion),
              },
            },
          );

          return {
            elementsVersion: Number(response.data?.data?.elementsVersion || 0),
          };
        },
      });
    },
  });

  const syncElements = useCallback(
    async (nextMap: Record<string, Element>) => {
      const baseData = getCachedElements();
      if (!baseData) {
        return;
      }

      try {
        await syncMutation.mutateAsync({
          baseData,
          nextMap,
        });
      } catch (error) {
        if (error instanceof ManualConflictResolutionError) {
          return;
        }
        throw error;
      }
    },
    [getCachedElements, syncMutation],
  );

  const createElement = useCallback(
    async (element: Element) => {
      const baseData = getCachedElements();
      if (!baseData) {
        return;
      }

      try {
        await createMutation.mutateAsync({
          baseData,
          element,
        });
      } catch (error) {
        if (error instanceof ManualConflictResolutionError) {
          return;
        }
        throw error;
      }
    },
    [createMutation, getCachedElements],
  );

  const updateElement = useCallback(
    async (elementId: string, config: Record<string, unknown>) => {
      const baseData = getCachedElements();
      if (!baseData) {
        return;
      }

      try {
        await updateMutation.mutateAsync({
          baseData,
          elementId,
          config,
        });
      } catch (error) {
        if (error instanceof ManualConflictResolutionError) {
          return;
        }
        throw error;
      }
    },
    [getCachedElements, updateMutation],
  );

  const deleteElement = useCallback(
    async (elementId: string) => {
      const baseData = getCachedElements();
      if (!baseData) {
        return;
      }

      try {
        await deleteMutation.mutateAsync({
          baseData,
          elementId,
        });
      } catch (error) {
        if (error instanceof ManualConflictResolutionError) {
          return;
        }
        throw error;
      }
    },
    [deleteMutation, getCachedElements],
  );

  return {
    syncElements,
    createElement,
    updateElement,
    deleteElement,
    hasUnsyncedChanges,
    clearUnsyncedChanges: () => setHasUnsyncedChanges(false),
  };
};
